// ── A NOTICE OF LOSS IS A CLAIM THE AGENT HAS ALREADY OPENED ────────────────
//
// Kevin, 2026-09-08: "Many people who have claims open are showing as not having
// a claim open. Brian Thompson is just one example" — and then, when asked which
// claim: "He has a corn silage appraisal claim opened right now."
//
// He is right, and the Claims page was not wrong about `indemnities`. It was
// answering a narrower question than the one being asked.
//
// BRIAN THOMPSON FARMS INC, NY-944-6076116-26, read live 2026-09-08:
//
//   indemnities   claim 00000068, CORN, status Paid, four disbursements
//                 (E $2,536 / F $30 / M $7,616 / E $1,033), checks 07-13 & 07-14
//   m13_notice_of_loss
//                 DL · commodity 0041 CORN · intention CS (chop/silage)
//                 · notice_date 2026-08-26
//
// The one CY2026 claim on that policy is his PREVENT-PLANT claim, settled in
// July. The corn silage appraisal was filed SIX WEEKS LATER and the AIP has not
// yet written a P20X stage record for it, so `indemnities` holds nothing at all
// and the page prints "Active 0 · Paid 1". Every word of that is true about the
// table and false about his claims.
//
// ⚠ THE ANSWER IS NOT TO MINT AN INDEMNITY ROW. This screen already reconciles
// its disbursements to the AIP's own stated claim total (M13 P20) and says so on
// its face; a synthetic $0 row would put a claim into that arithmetic that the
// carrier never issued. A filed notice is a DIFFERENT STATE — the agent has
// opened it, the adjuster has not picked it up — and it gets its own lane.
//
// The 2026-06-16 note in App.jsx that removed the previous attempt said exactly
// this: "If early-notice visibility is wanted later, build it as a separate,
// de-duplicated, clearly-labeled section." That attempt read P12, which CLAUDE.md
// has since corrected — P12 is the PAYMENT record, not the notice. P58 is the
// Notice of Loss, and `m13_notice_of_loss` is its raw mirror, carrying notice
// type, damage date, notice date, intention and commodity for BOTH carriers.
//
// MEASURED BY REPLAYING THIS RULE over the live book, CY2026, 2026-09-08
// (tools/notice-book-report.js). 199 notice cells (policy x commodity):
//
//   102  a claim on that crop is already ACTIVE   -> the Active tab shows it
//    77  a claim settled ON OR AFTER the notice   -> the Paid tab shows it
//     0  named a policy this book does not hold
//     0  carried a commodity the map cannot name
//
//   ---> 20 OUTSTANDING cells across 17 farms, EVERY ONE OF THEM NAU.
//
// Eleven carry the CS (chop / silage) intention, filed 2026-08-26 and
// 2026-09-01 — the corn silage appraisals Kevin was asking about, on Acomb,
// Anchor, Anderson Farms Dairy, Brian Thompson, Cripple Creek, Hartway,
// Lefeber, My-T Acres, Rauberdale, Thomas Nagle and Verratti. The rest are five
// June prevent-plant notices and four July harvest notices.
//
// ⚠ AND FOUR OF THE TWELVE COMMODITY CODES THE MIRROR SENDS WERE MIS-NAMED WHEN
// I TYPED THEM BY HAND while measuring this: 0084 is POTATOES not sunflowers,
// 0072 is CABBAGE not seed corn, 0054 is APPLES not cabbage, 0158 is TRITICALE
// not apples. Every one is in `src/rmaCrops.js` already, sourced from RMA's own
// document numbering. Correcting them moved eight rows OFF this list, because
// with the right name they match a claim that already exists. That is why this
// module takes the map as an argument rather than carrying its own — a rule that
// types its own dictionary is wrong four times in twelve and confident
// throughout.

// ── THE JOIN KEY ────────────────────────────────────────────────────────────
//
// ⚠ SYNC-FORMAT ONLY, and that is a correctness rule rather than tidiness. The
// mirror stores the BARE policy number — NAU `1024224` against the book's
// `NY-944-1024224-26`, R&H `0805227` against `MP-0805227` — so the book side has
// to be reduced to its core. Applied to the hand-loaded carriers that reduction
// MIS-PARSES: Agri Sompo's `2026-NY-601-838587` and ProAg's `36-415-1050573`
// both collapse to their AGENCY number, which collides across 8 policies / 6
// farms. Those carriers have no M13 file and so can never have a notice row —
// giving them no key is both safe and honest.
//
// Measured: 480 sync-format CY2026 policies, ZERO colliding keys.
//
// This is the rule the corn chop lane already ran on, lifted out of
// ClaimManagerView so both callers share it. A key defined twice is a key that
// drifts once — the pattern this repo has recorded for three news handlers, two
// guarantee engines and three copies of normFarmerName.
function noticeKeyForPolicy(policyNumber, aip, isSyncFormat) {
  const pn = String(policyNumber || "").trim();
  if (typeof isSyncFormat !== "function") throw new Error("noticeKeyForPolicy needs the sync-format test");
  if (!isSyncFormat(pn)) return null;   // hand-loaded — no file, no notice
  const parts = pn.split("-");
  // NAU  ST-AGENCY-POLICY-YY -> the third segment; R&H  MP-POLICY -> the second.
  const core = parts.length >= 4 ? parts[2] : parts[1];
  const bare = String(core || "").replace(/^0+/, "");
  if (!bare) return null;
  return `${String(aip || "").toUpperCase()}|${bare}`;
}

// The same key from the mirror side, where the number is already bare.
function noticeKeyForMirror(row) {
  return `${String((row && row.aip) || "").toUpperCase()}|${String((row && row.policy_number) || "").trim().replace(/^0+/, "")}`;
}

// ── RMA ICE CODE MEANINGS ───────────────────────────────────────────────────
// Sourced, never guessed. Notice type is P58 field 22; intention is field 26,
// ICE D00067. A code that is not listed renders RAW — the convention this repo
// follows for the claim stage codes, the corn type codes and the insurance
// option codes, and the reason `0034` stopped surfacing peaches on an apple
// policy.
const NOTICE_TYPE_NAME = { DL: "Damage", PP: "Prevented planting" };
const NOTICE_INTENTION_NAME = {
  HT: "Harvest",
  RP: "Replant",
  DS: "Destroy",
  CS: "Chop / silage",
  OT: "Other",
  LC: "Left crop standing",
};

// A comma-joined crop LABEL split into its atoms. `indemnities.crop` is joined
// ("CORN, SOYBEANS") because the AIP folds several coverages onto one claim, so
// a substring test would let DRY BEANS satisfy a SOYBEANS notice and SEED CORN
// satisfy a CORN one. Tokens, compared whole.
function cropTokens(label) {
  return String(label || "").split(",").map((x) => x.trim().toUpperCase()).filter(Boolean);
}

function dateVal(d) {
  if (!d) return null;
  const t = Date.parse(String(d).slice(0, 10));
  return Number.isFinite(t) ? t : null;
}

/**
 * Which filed notices have NOT yet turned into visible claim work?
 *
 * One row per (policy, commodity) — a policy carries several notices for one
 * commodity (Anderson Farms Dairy has four) and listing each would read as four
 * separate claims.
 *
 * A notice is REPORTED unless one of two things is true, and both are the claim
 * being visible somewhere else rather than the notice being unimportant:
 *
 *   1. an Open / Pending / In Adjustment claim already names that crop on that
 *      policy — the Active tab is already showing it; or
 *   2. a claim on that crop settled ON OR AFTER the notice date — the work is
 *      done, and the Paid tab is showing it.
 *
 * ⚠ THE DATE COMPARISON IS THE WHOLE OF BRIAN THOMPSON'S CASE. Dropping it makes
 * "this policy has a paid corn claim" suppress a corn notice filed six weeks
 * after that claim settled, which is the defect this module exists to fix. Going
 * the other way — ignoring settled claims entirely — puts every farm whose
 * notice preceded its own payment back on a call list they have finished with.
 *
 * ⚠ A CLAIM WITH NO **AIP** DATE CANNOT SETTLE A NOTICE, and the obvious field
 * is the wrong one. The Claims page's own `activityDate` falls back to
 * `created_at` — the timestamp WE wrote the row, not anything the carrier said —
 * and 25 of the 364 CY2026 indemnity rows carry neither a check date nor a claim
 * date, so on those `created_at` is today and would clear every notice on that
 * crop the moment a sync ran. Only `checkDate` and `claimDate` are the AIP
 * speaking. A row with neither settles nothing, which fails toward REPORTING:
 * a false positive costs a look, a false negative costs a grower.
 *
 * ⚠ AND "ALREADY VISIBLE" IS THE ACTIVE TAB'S OWN TEST, PASSED IN. The tab is
 * `isActiveStatus = s !== "Paid" && s !== "Closed"` — a NEGATIVE rule, so a
 * status nobody has seen yet is active by default. A positive whitelist here
 * would drift from it in the one direction that matters: a claim this lane
 * suppresses and that tab does not show is invisible again, which is the whole
 * defect.
 */
function outstandingNotices({ notices, policies, claims, cropYear, isSyncFormat, cropForCode, isActiveStatus }) {
  const year = Number(cropYear);
  if (!Number.isFinite(year)) throw new Error("outstandingNotices needs a crop year");
  if (typeof cropForCode !== "function") throw new Error("outstandingNotices needs the commodity map");
  if (typeof isActiveStatus !== "function") throw new Error("outstandingNotices needs the Active tab's own status test");

  // policy key -> the policy rows it names (a farm may hold the same core under
  // two crop years; only this year's are in play).
  const byKey = new Map();
  for (const p of policies || []) {
    if (Number(p && p.cropYear) !== year) continue;
    const k = noticeKeyForPolicy(p.policyNumber, p.aip, isSyncFormat);
    if (!k) continue;
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(p);
  }

  // policyNumber -> this year's claims on it
  const claimsByPolicy = new Map();
  for (const c of claims || []) {
    if (Number(c && c.cropYear) !== year) continue;
    const pn = String((c && c.policyNumber) || "");
    if (!pn) continue;
    if (!claimsByPolicy.has(pn)) claimsByPolicy.set(pn, []);
    claimsByPolicy.get(pn).push(c);
  }

  const cells = new Map();      // policyNumber|commodity -> cell
  let unjoined = 0;             // notices naming a policy this book does not hold

  for (const n of notices || []) {
    if (Number(n && n.crop_year) !== year) continue;
    const pols = byKey.get(noticeKeyForMirror(n));
    if (!pols || !pols.length) { unjoined++; continue; }
    const code = String((n && n.commodity_code) || "").trim();
    for (const p of pols) {
      const key = `${p.policyNumber}|${code}`;
      let cell = cells.get(key);
      if (!cell) {
        cell = {
          farmerId: p.farmerId || null,
          farmerName: p.farmerName || "",
          policyId: p.id || null,
          policyNumber: p.policyNumber,
          aip: p.aip || n.aip || "",
          commodityCode: code,
          // A code the map cannot name renders RAW rather than as a blank or a
          // guess — the same convention every other unsourced code follows here.
          crop: cropForCode(code) || null,
          noticeTypes: new Set(),
          intentions: new Set(),
          noticeCount: 0,
          noticeDate: null,
          damageDate: null,
        };
        cells.set(key, cell);
      }
      cell.noticeCount++;
      const nt = String((n && n.notice_type_code) || "").trim().toUpperCase();
      if (nt) cell.noticeTypes.add(nt);
      for (const it of String((n && n.insured_intention_codes) || "").toUpperCase().split(/[^A-Z]+/)) {
        if (it) cell.intentions.add(it);
      }
      const nd = dateVal(n && n.notice_date);
      if (nd != null && (cell.noticeDate == null || nd > cell.noticeDate)) cell.noticeDate = nd;
      const dd = dateVal(n && n.damage_date);
      if (dd != null && (cell.damageDate == null || dd > cell.damageDate)) cell.damageDate = dd;
    }
  }

  const out = [];
  let suppressedOpen = 0, suppressedSettled = 0, unnamedCommodity = 0;

  for (const cell of cells.values()) {
    const mine = (claimsByPolicy.get(cell.policyNumber) || []).filter((c) => {
      // ⚠ A COMMODITY THE MAP CANNOT NAME MATCHES NOTHING, so the notice is
      // reported rather than silently cleared by an unrelated claim.
      if (!cell.crop) return false;
      return cropTokens(c.crop).includes(cell.crop);
    });
    if (mine.some((c) => isActiveStatus(c.status))) { suppressedOpen++; continue; }
    const settledAfter = mine.some((c) => {
      const d = dateVal(c.checkDate || c.claimDate);
      return d != null && cell.noticeDate != null && d >= cell.noticeDate;
    });
    if (settledAfter) { suppressedSettled++; continue; }
    if (!cell.crop) unnamedCommodity++;
    out.push({
      ...cell,
      noticeTypes: [...cell.noticeTypes].sort(),
      intentions: [...cell.intentions].sort(),
      // A settled claim on this crop that PREDATES the notice is context, not a
      // reason to hide the row: it is why the page would otherwise read "Paid 1".
      priorPaid: mine.length,
    });
  }

  out.sort((a, b) => (b.noticeDate || 0) - (a.noticeDate || 0) ||
    String(a.farmerName).localeCompare(String(b.farmerName)));

  return {
    rows: out,
    cells: cells.size,
    suppressedOpen,
    suppressedSettled,
    unnamedCommodity,
    unjoined,
    farms: new Set(out.map((r) => r.farmerId || r.policyNumber)).size,
  };
}

// ── WHO HAS A SILAGE APPRAISAL OPEN ─────────────────────────────────────────
//
// Kevin's original ask on the All Customers claim filter was not "has a claim" —
// it was: "what I want to quickly see is who grows corn but doesn't have a
// silage appraisal claim opened?" The generic has/no-claim chips answer a
// NEARBY question, and the difference is not small. Measured on the live book,
// CY2026: 199 growers carry corn, 58 have a silage-appraisal notice, 141 do not
// — and 67 of those 141 DO carry some other 2026 claim, so a plain "no claim"
// chip hides them. Brian Thompson is one of the 67: a prevent-plant claim
// settled in July and a chop notice filed 2026-08-26.
//
// ⚠ CS IS THE MARKER AND IT IS SOURCED, not inferred — RMA ICE D00067
// IceInsuredIntention, carried on the P58 Notice of Loss field 26. A 2026-08-02
// session hunted for a chop marker, ruled out four candidates and concluded none
// existed; this is it, and the column had been synced the whole time.
//
// ⚠ THE TWO SOURCES ARE UNIONED, NEVER SWAPPED, and that rule is load-bearing in
// one direction only. The claim column is written by m13-sync and BY NOTHING ELSE
// — nau-sync has no such line — so reading it alone showed 14 R&H farms and none
// of NAU's. And it DECAYS: a claim set to Released loses every P58-derived field
// (0 of 47 CY2025 and 0 of 14 CY2026 Released claims carry an intention), so a
// cohort read off the claim row silently shrinks as claims settle. The notice
// mirror covers both carriers and does not decay. This is a list of farms to go
// and work: a false negative costs a grower, a false positive costs a look.
const SILAGE_INTENTION = "CS";

const hasIntention = (codes, want) =>
  String(codes || "").toUpperCase().split(/[^A-Z]+/).includes(want);

/**
 * Farm ids with a silage appraisal open on one commodity, from EITHER source.
 *
 * `commodityCode` is matched, never widened — a chop notice on soybeans must not
 * put a farm on the corn list — and `crop` does the same on the claim side, where
 * the label is comma-joined ("CORN, SOYBEANS") so the atoms are compared whole
 * rather than as substrings.
 */
function silageAppraisalFarms({ notices, policies, claims, cropYear, commodityCode, crop, isSyncFormat }) {
  const year = Number(cropYear);
  if (!Number.isFinite(year)) throw new Error("silageAppraisalFarms needs a crop year");
  const want = String(commodityCode || "").replace(/^0+/, "");
  const wantCrop = String(crop || "").toUpperCase();

  const byKey = new Map();          // notice key -> farmer ids
  const farmerOfPolicy = new Map(); // policy id  -> farmer id
  for (const p of policies || []) {
    if (Number(p && p.cropYear) !== year) continue;
    if (p.id != null && p.farmerId != null) farmerOfPolicy.set(String(p.id), p.farmerId);
    const k = noticeKeyForPolicy(p.policyNumber, p.aip, isSyncFormat);
    if (!k || p.farmerId == null) continue;
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(p.farmerId);
  }

  const out = new Set();
  let fromNotices = 0, fromClaims = 0;
  for (const n of notices || []) {
    if (Number(n && n.crop_year) !== year) continue;
    if (!hasIntention(n && n.insured_intention_codes, SILAGE_INTENTION)) continue;
    if (want && String((n && n.commodity_code) || "").replace(/^0+/, "") !== want) continue;
    for (const f of byKey.get(noticeKeyForMirror(n)) || []) { if (!out.has(f)) fromNotices++; out.add(f); }
  }
  for (const c of claims || []) {
    if (Number(c && c.cropYear) !== year) continue;
    if (!hasIntention(c && c.intentionCodes, SILAGE_INTENTION)) continue;
    if (wantCrop && !cropTokens(c && c.crop).includes(wantCrop)) continue;
    const f = farmerOfPolicy.get(String(c && c.policyId));
    if (f != null) { if (!out.has(f)) fromClaims++; out.add(f); }
  }
  return { farms: out, fromNotices, fromClaims };
}

// ⚠ THE COLUMNS THIS RULE READS, NAMED BY THE RULE. A PostgREST select that
// omits one does not error — the field arrives undefined and the rule quietly
// answers a different question. That is the defect this repo has already paid
// for twice: `price_election_pct` never selected on four crop_coverage reads
// (three grain farms read 45% short), and `state_code` never selected on the DRP
// board (n_obs = 0 on all 30 cells while the offline fixture passed).
const NOTICE_SELECT_COLUMNS =
  "aip,policy_number,commodity_code,notice_type_code,insured_intention_codes,notice_date,damage_date,crop_year";

// The notice columns the silage rule reads. Same discipline as the lane above:
// a select that omits one does not error, it answers a different question.
const SILAGE_NOTICE_COLUMNS =
  "aip,policy_number,commodity_code,insured_intention_codes,crop_year";

module.exports = {
  NOTICE_SELECT_COLUMNS,
  SILAGE_NOTICE_COLUMNS,
  SILAGE_INTENTION,
  silageAppraisalFarms,
  hasIntention,
  noticeKeyForPolicy,
  noticeKeyForMirror,
  outstandingNotices,
  cropTokens,
  NOTICE_TYPE_NAME,
  NOTICE_INTENTION_NAME,
};
